import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  Building2,
  CalendarDays,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  Droplets,
  Menu,
  Gauge,
  LogOut,
  FileCheck2,
  Settings,
  ShieldCheck,
  Smartphone,
  Warehouse,
  Waves,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@shared/components/LanguageSwitcher';
import { OfflineBanner } from '@shared/offline/OfflineBanner';
import { SyncStatusPanel } from '@features/sync/components/SyncStatusPanel';
import { useSyncOnReconnect } from '@features/sync/hooks/useSyncOnReconnect';
import { StationSelector } from '@features/stations/components/StationSelector';
import { useAuth } from '@/hooks/useAuth';
import { env } from '@/config/env';
import { BillingStatusBanner } from '@features/billing/components/BillingStatusBanner';
import { UpdateAvailableBanner } from '@features/appVersion/components/UpdateAvailableBanner';
import { MaintenanceBanner } from '@features/appVersion/components/MaintenanceBanner';
import { useMaintenanceState } from '@features/appVersion/hooks/useMaintenanceState';
import { useSelectedStation } from '@features/stations/hooks/useSelectedStation';

type SidebarLink = {
  type: 'link';
  to: string;
  icon: typeof Gauge;
  labelKey: string;
  end?: boolean;
};

type SidebarGroup = {
  type: 'group';
  id: string;
  icon: typeof Gauge;
  labelKey: string;
  children: SidebarLink[];
};

type SidebarItem = SidebarLink | SidebarGroup;

const buildNavigation = (selectedStationId?: string): SidebarItem[] => [
  { type: 'link', to: 'dashboard', icon: Gauge, labelKey: 'nav.dashboard', end: true },
  { type: 'link', to: 'compliance/calendar', icon: CalendarDays, labelKey: 'nav.calendar' },
  {
    type: 'group',
    id: 'fuel-receptions',
    icon: Droplets,
    labelKey: 'nav.fuelReceptions',
    children: [
      {
        type: 'link',
        to: 'fuel-receptions',
        icon: SearchIcon,
        labelKey: 'nav.fuelReceptionSearch',
        end: true
      },
      {
        type: 'link',
        to: 'fuel-receptions/new',
        icon: Droplets,
        labelKey: 'nav.fuelReceptionNew'
      },
      {
        type: 'link',
        to: 'reports/official',
        icon: FileCheck2,
        labelKey: 'nav.officialReports'
      }
    ]
  },
  {
    type: 'group',
    id: 'well-monitoring',
    icon: Waves,
    labelKey: 'nav.wellMonitoring',
    children: [
      {
        type: 'link',
        to: 'monitoring/inspections',
        icon: ClipboardCheck,
        labelKey: 'nav.inspectionHistory',
        end: true
      },
      {
        type: 'link',
        to: 'monitoring/inspections/new',
        icon: ClipboardCheck,
        labelKey: 'nav.inspectionNew'
      },
      {
        type: 'link',
        to: 'monitoring/wells',
        icon: Waves,
        labelKey: 'nav.wells'
      }
    ]
  },
  ...(env.billingEnabled
    ? [{ type: 'link' as const, to: 'billing', icon: CreditCard, labelKey: 'nav.billing' }]
    : []),
  {
    type: 'group',
    id: 'settings',
    icon: Settings,
    labelKey: 'nav.settings',
    children: [
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/compliance-profile` : 'dashboard',
        icon: Building2,
        labelKey: 'nav.companyData'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/areas` : 'dashboard',
        icon: Warehouse,
        labelKey: 'nav.areas'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/tanks` : 'dashboard',
        icon: Droplets,
        labelKey: 'nav.tanks'
      },
      {
        type: 'link',
        to: selectedStationId ? `stations/${selectedStationId}/dispensers` : 'dashboard',
        icon: Gauge,
        labelKey: 'nav.dispensers'
      },
      {
        type: 'link',
        to: 'settings/sessions',
        icon: Smartphone,
        labelKey: 'nav.sessions'
      }
    ]
  }
];

const SearchIcon = Activity;

export const AppLayout = () => {
  const { t } = useTranslation();
  const { lng = 'es' } = useParams();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const maintenance = useMaintenanceState();
  const { selectedStationId } = useSelectedStation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useSyncOnReconnect();

  const handleLogout = async () => {
    await logout();
    void navigate(`/${lng}/login`, { replace: true });
  };

  return (
    <div className="min-h-screen bg-slate-50 text-ink">
      <OfflineBanner />
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-72 border-r border-slate-800 bg-shell text-white lg:block">
        <SidebarContent
          lng={lng}
          selectedStationId={selectedStationId}
          onNavigate={() => undefined}
        />
      </aside>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <button
            className="absolute inset-0 bg-slate-950/55"
            type="button"
            aria-label={t('nav.closeMenu')}
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="relative flex h-full w-[min(86vw,20rem)] flex-col border-r border-slate-800 bg-shell text-white shadow-2xl">
            <div className="flex h-16 items-center justify-between border-b border-white/10 pr-3">
              <SidebarBrand />
              <button
                className="grid h-10 w-10 place-items-center rounded-md text-slate-200 hover:bg-white/10"
                type="button"
                aria-label={t('nav.closeMenu')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <SidebarNav
              lng={lng}
              selectedStationId={selectedStationId}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </aside>
        </div>
      ) : null}

      <div className="lg:pl-72">
        <header className="sticky top-0 z-10 border-b border-line bg-white/95 backdrop-blur">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <button
                className="toolbar-button lg:hidden"
                type="button"
                aria-label={t('nav.openMenu')}
                title={t('nav.openMenu')}
                onClick={() => setMobileMenuOpen(true)}
              >
                <Menu className="h-4 w-4" aria-hidden="true" />
              </button>
              <Activity className="h-5 w-5 text-brand-600" aria-hidden="true" />
              <span className="hidden text-sm font-semibold sm:inline">{t('app.workspace')}</span>
            </div>
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <StationSelector />
              {user ? (
                <span className="hidden text-sm text-muted md:inline">{user.email}</span>
              ) : null}
              <SyncStatusPanel compact />
              <LanguageSwitcher />
              <button
                className="toolbar-button"
                type="button"
                aria-label={t('auth.logout')}
                title={t('auth.logout')}
                onClick={() => void handleLogout()}
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </div>
        </header>
        {maintenance ? (
          <MaintenanceBanner
            level={maintenance.level}
            retryAfterSeconds={maintenance.retryAfterSeconds}
          />
        ) : null}
        <main className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-4 space-y-3">
            <UpdateAvailableBanner />
            <BillingStatusBanner />
          </div>
          <Outlet />
        </main>
        <footer className="border-t border-line px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-2 text-xs text-muted sm:flex-row">
            <span>© {new Date().getFullYear()} FuelOps</span>
            <nav className="flex items-center gap-3">
              <Link
                to={`/${lng}/legal/privacy`}
                className="underline underline-offset-2 hover:text-ink"
              >
                {t('legal.privacyTitle')}
              </Link>
              <span aria-hidden="true">·</span>
              <Link
                to={`/${lng}/legal/terms`}
                className="underline underline-offset-2 hover:text-ink"
              >
                {t('legal.termsTitle')}
              </Link>
            </nav>
          </div>
        </footer>
      </div>
    </div>
  );
};

const SidebarBrand = () => {
  const { t } = useTranslation();

  return (
    <div className="flex h-16 items-center gap-3 px-5">
      <div className="grid h-9 w-9 place-items-center rounded-md bg-brand-500">
        <ShieldCheck className="h-5 w-5" aria-hidden="true" />
      </div>
      <div>
        <p className="text-sm font-semibold">FuelOps</p>
        <p className="text-xs text-slate-300">{t('app.tagline')}</p>
      </div>
    </div>
  );
};

const SidebarNav = ({
  lng,
  selectedStationId,
  onNavigate
}: {
  lng: string;
  selectedStationId?: string;
  onNavigate: () => void;
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const items = useMemo(() => buildNavigation(selectedStationId), [selectedStationId]);
  const activeGroupIds = useMemo(
    () =>
      items
        .filter((item): item is SidebarGroup => item.type === 'group')
        .filter((item) =>
          item.children.some((child) => location.pathname.startsWith(`/${lng}/${child.to}`))
        )
        .map((item) => item.id),
    [items, lng, location.pathname]
  );
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(activeGroupIds));

  useEffect(() => {
    if (activeGroupIds.length === 0) {
      return;
    }

    setOpenGroups((current) => {
      const next = new Set(current);
      activeGroupIds.forEach((id) => next.add(id));
      return next;
    });
  }, [activeGroupIds]);

  const toggleGroup = (groupId: string) => {
    setOpenGroups((current) => {
      const next = new Set(current);

      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }

      return next;
    });
  };

  return (
    <nav className="space-y-1 px-3 py-4">
      {items.map((item) => {
        if (item.type === 'link') {
          return <SidebarNavLink key={item.to} item={item} lng={lng} onNavigate={onNavigate} />;
        }

        const Icon = item.icon;
        const isOpen = openGroups.has(item.id);
        const isActive = activeGroupIds.includes(item.id);

        return (
          <div key={item.id}>
            <button
              type="button"
              className={[
                'flex min-h-11 w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition',
                isActive
                  ? 'bg-white/12 text-white'
                  : 'text-slate-300 hover:bg-white/8 hover:text-white'
              ].join(' ')}
              aria-expanded={isOpen}
              onClick={() => toggleGroup(item.id)}
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              <span className="min-w-0 flex-1">{t(item.labelKey)}</span>
              <ChevronDown
                className={[
                  'h-4 w-4 transition-transform',
                  isOpen ? 'rotate-180 text-white' : 'text-slate-400'
                ].join(' ')}
                aria-hidden="true"
              />
            </button>
            {isOpen ? (
              <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
                {item.children.map((child) => (
                  <SidebarNavLink
                    key={`${item.id}-${child.to}-${child.labelKey}`}
                    item={child}
                    lng={lng}
                    onNavigate={onNavigate}
                    nested
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
};

const SidebarNavLink = ({
  item,
  lng,
  onNavigate,
  nested
}: {
  item: SidebarLink;
  lng: string;
  onNavigate: () => void;
  nested?: boolean;
}) => {
  const { t } = useTranslation();
  const Icon = item.icon;

  return (
    <NavLink
      to={`/${lng}/${item.to}`}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition',
          nested ? 'text-xs' : 'text-sm',
          isActive ? 'bg-white/12 text-white' : 'text-slate-300 hover:bg-white/8 hover:text-white'
        ].join(' ')
      }
    >
      <Icon className={nested ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
      {t(item.labelKey)}
    </NavLink>
  );
};

const SidebarContent = ({
  lng,
  selectedStationId,
  onNavigate
}: {
  lng: string;
  selectedStationId?: string;
  onNavigate: () => void;
}) => (
  <>
    <div className="border-b border-white/10">
      <SidebarBrand />
    </div>
    <SidebarNav lng={lng} selectedStationId={selectedStationId} onNavigate={onNavigate} />
  </>
);
